import React, { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBringForward } from '@fortawesome/pro-regular-svg-icons/faBringForward';
import { Popover } from 'antd';
import { Tooltip } from 'src/components/core';
import { usePopoverFocus } from 'src/components/design-system/popover/hooks/use-popover-focus';
import { theme } from 'shared/constants/theme';
import classNames from 'classnames';
import variantStyles from './mirror-badge.module.scss';

/**
 * Badge that indicates a category is part of a mirror relationship.
 *
 * - `variant: 'mirror'` (default): shown ON a mirror category. Hover tooltip with
 *   plain-text path info.
 * - `variant: 'source'`: shown ON a source category. Click popover — its content
 *   has clickable links to each mirror, so click is needed to interact reliably.
 *
 * When `label` is missing only the icon renders, letting an ancestor own the
 * affordance.
 */
export function MirrorBadge({ label, className, iconClassName, variant = 'mirror' }) {
  const isSource = variant === 'source';
  const icon = (
    <FontAwesomeIcon
      icon={faBringForward}
      className={classNames(
        variantStyles.defaultIcon,
        isSource && variantStyles.sourceVariant,
        className,
        iconClassName,
      )}
    />
  );

  if (!label) {
    return icon;
  }

  if (isSource) {
    return <SourceBadge label={label}>{icon}</SourceBadge>;
  }

  return <TargetBadge label={label}>{icon}</TargetBadge>;
}

function TargetBadge({ label, children }) {
  return <Tooltip title={label}>{children}</Tooltip>;
}

function SourceBadge({ label, children }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const openedByKeyboardRef = useRef(false);

  const handleClose = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  usePopoverFocus(contentRef, handleClose, open);

  const handleAfterOpenChange = useCallback(isOpen => {
    if (!isOpen) {
      openedByKeyboardRef.current = false;
      return;
    }
    if (!openedByKeyboardRef.current) return;
    openedByKeyboardRef.current = false;
    const firstLink = contentRef.current?.querySelector('a[href]');
    (firstLink ?? contentRef.current)?.focus();
  }, []);

  const stopPropagation = useCallback(e => e.stopPropagation(), []);

  const handleKeyDown = useCallback(e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      openedByKeyboardRef.current = true;
      e.currentTarget.click();
    }
  }, []);

  const handleOpenChange = useCallback(newOpen => {
    setOpen(newOpen);
  }, []);

  return (
    <Popover
      content={
        <div ref={contentRef} className={variantStyles.popoverContent} tabIndex={-1}>
          {label}
        </div>
      }
      open={open}
      onOpenChange={handleOpenChange}
      afterOpenChange={handleAfterOpenChange}
      trigger={['hover', 'click']}
      // Match the tooltip's dark look so the badge variants feel consistent;
      // also let it grow past the default cap to fit the longest path line.
      color={theme.tooltipColor}
      overlayInnerStyle={{ maxWidth: 'none', width: 'max-content' }}>
      <span
        ref={triggerRef}
        role="button"
        tabIndex={0}
        onClick={stopPropagation}
        onKeyDown={handleKeyDown}
        className={variantStyles.popoverTrigger}>
        {children}
      </span>
    </Popover>
  );
}
